import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "status"]
  static values = { url: String, path: String }

  connect() {
    this.uploading = false
  }

  dragover(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    this.dropzoneTarget.classList.add("border-stone-500", "bg-stone-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-stone-500", "bg-stone-50")
  }

  async drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-stone-500", "bg-stone-50")

    if (this.uploading) return
    this.uploading = true

    const items = event.dataTransfer.items
    const files = []

    // Collect all files (including from directories) via webkitGetAsEntry
    const promises = []
    for (const item of items) {
      const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null
      if (entry) {
        promises.push(this.readEntry(entry, "", files))
      }
    }

    await Promise.all(promises)

    if (files.length === 0) {
      this.uploading = false
      return
    }

    this.setStatus(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}...`)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    let uploaded = 0
    let failed = 0

    for (const { file, relativePath } of files) {
      try {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("relative_path", relativePath)
        formData.append("path", this.pathValue)

        const response = await fetch(this.urlValue, {
          method: "POST",
          headers: {
            "X-CSRF-Token": csrfToken,
            "Accept": "application/json",
          },
          body: formData,
        })

        if (response.ok) {
          uploaded++
        } else {
          failed++
          const data = await response.json().catch(() => ({}))
          console.error(`Upload failed for ${relativePath}:`, data.error || response.statusText)
        }
      } catch (error) {
        failed++
        console.error(`Upload error for ${relativePath}:`, error)
      }

      this.setStatus(`Uploaded ${uploaded}/${files.length}${failed > 0 ? ` (${failed} failed)` : ""}...`)
    }

    const summary = failed > 0
      ? `Done: ${uploaded} uploaded, ${failed} failed.`
      : `Done: ${uploaded} file${uploaded === 1 ? "" : "s"} uploaded.`

    this.setStatus(summary)
    this.uploading = false

    // Reload to show new files
    if (uploaded > 0) {
      setTimeout(() => { window.location.reload() }, 1000)
    }
  }

  readEntry(entry, basePath, files) {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file) => {
          const relativePath = basePath ? `${basePath}/${entry.name}` : entry.name
          files.push({ file, relativePath })
          resolve()
        })
      } else if (entry.isDirectory) {
        const reader = entry.createReader()
        this.readAllEntries(reader, [], (entries) => {
          const dirPath = basePath ? `${basePath}/${entry.name}` : entry.name
          const promises = entries.map((child) => this.readEntry(child, dirPath, files))
          Promise.all(promises).then(resolve)
        })
      } else {
        resolve()
      }
    })
  }

  // readEntries only returns up to 100 entries at a time, so we must loop
  readAllEntries(reader, accumulated, callback) {
    reader.readEntries((entries) => {
      if (entries.length > 0) {
        this.readAllEntries(reader, accumulated.concat(Array.from(entries)), callback)
      } else {
        callback(accumulated)
      }
    })
  }

  setStatus(text) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = text
    }
  }
}
